import React, { useState, useEffect } from 'react';
import Loading from '../../Common/Loading';

interface Subject {
  value: string;
  text: string;
}

interface Country {
  value: string;  // display name from EL_MD_DicCountryYear (e.g. "Australia", "AUSVELS")
  text: string;   // same as value — shown in dropdown
}

interface SubStrand {
  Strand: string;
  SubStrand: string;
  Description: string;
  NoOfELMDMWLinks: number;
}

interface DicDetail {
  DicFileName: string;
  UnitCode: string;
  Unit: string;
  ChapterCode: string;
  Chapter: string;
  Activity: string;
  LineNum: string;
  LessonCode: string;
  LessonTime: number | null;
  TimesLinked: number | null;
  ELFlagged: boolean;
  MDFlagged: boolean;
  IgnoreLesson: boolean;
  SelectiveLesson: boolean;
  RowType: 'dicfile' | 'unit' | 'chapter' | 'lesson';
}

const EL_LinkMDandMW: React.FC = () => {
  const [year, setYear] = useState<string>('0');
  // elCurriculum = display name used in all EL_* table queries AND as the country identifier
  // (MD short-code mapping is handled entirely server-side in views.py)
  const [elCurriculum, setElCurriculum] = useState<string>('');
  const [subject, setSubject] = useState<string>('Mathematics');
  const [countries, setCountries] = useState<Country[]>([]);
  const [subStrands, setSubStrands] = useState<SubStrand[]>([]);
  const [dicDetails, setDicDetails] = useState<DicDetail[]>([]);
  const [selectedSubStrand, setSelectedSubStrand] = useState<SubStrand | null>(null);
  const [selectedDicLsns, setSelectedDicLsns] = useState<Set<string>>(new Set());
  const [errorMesg, setErrorMesg] = useState<string>('');
  const [alertMesg, setAlertMesg] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [linkedLessons, setLinkedLessons] = useState<any[]>([]);

  const subjects: Subject[] = [
    { value: 'Mathematics', text: 'Mathematics' },
    { value: 'English',     text: 'English' },
    { value: 'Science',     text: 'Science' },
  ];

  const years: string[] = Array.from({ length: 13 }, (_, i) => i.toString());
  document.body.style.margin = '5px 100px 5px 10px';
  document.body.style.marginRight = '100px';
  const API_BASE = process.env.REACT_APP_DOCUMENT_LINK || 'https://staging.sysadmin.elearnoncloud.com/';

  useEffect(() => {
    document.body.style.backgroundColor = '#ffffff';
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}api/md_countries/`);
      if (!response.ok) throw new Error('Failed to fetch countries');
      const data: Country[] = await response.json();
      setCountries(data);
      if (data.length > 0) {
        setElCurriculum(data[0].value);
      }
    } catch (error) {
      console.error('Failed to fetch countries:', error);
      setErrorMesg('Failed to fetch countries. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoClick = async () => {
    if (!year || !elCurriculum || !subject) {
      setErrorMesg('Please select Year, Country, and Subject.');
      return;
    }
    setErrorMesg('');
    setAlertMesg('');
    setSelectedSubStrand(null);
    setSelectedDicLsns(new Set());
    // Fire both in parallel — same as VB calling fn_LoadData once which does both queries
    await Promise.all([fetchSubStrands(), fetchDicDetails()]);
  };

  const fetchSubStrands = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ year, elCurriculum, subject });
      const response = await fetch(`${API_BASE}api/substrands/?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to fetch sub-strands');
      const data = await response.json();
      setSubStrands(data);
    } catch (error) {
      console.error('Failed to fetch sub-strands:', error);
      setErrorMesg('Failed to fetch sub-strands. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDicDetails = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ year, elCurriculum, subject });
      const response = await fetch(`${API_BASE}api/dicdetails/?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to fetch dictionary details');
      const data = await response.json();
      setDicDetails(data);
    } catch (error) {
      console.error('Failed to fetch dictionary details:', error);
      setErrorMesg('Failed to fetch dictionary details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubStrandClick = async (ss: SubStrand) => {
    setSelectedSubStrand(ss);
    setSelectedDicLsns(new Set());
    await fetchLinksForSubStrand(ss);
  };

  const fetchLinksForSubStrand = async (ss: SubStrand) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        elCurriculum,
        subject,
        year,
        strand:    ss.Strand,
        subStrand: ss.SubStrand,
      });
      const response = await fetch(`${API_BASE}api/getlinks/?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to fetch links');
      const data = await response.json();
      setLinkedLessons(data);

      const linkedCodes = new Set<string>();
      data.forEach((link: any) => {
        linkedCodes.add(`${link.DicFileName}^${link.LineNum}^${link.LessonCode}`);
      });
      setSelectedDicLsns(linkedCodes);

      setTimeout(() => {
        const firstChecked = document.querySelector('input[type="checkbox"]:checked');
        if (firstChecked) {
          firstChecked.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    } catch (error) {
      console.error('Failed to fetch links:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDicLsnToggle = (dicLsnCode: string) => {
    const newSelected = new Set(selectedDicLsns);
    if (newSelected.has(dicLsnCode)) {
      newSelected.delete(dicLsnCode);
    } else {
      newSelected.add(dicLsnCode);
    }
    setSelectedDicLsns(newSelected);
  };

  const handleSetLinks = async () => {
    if (!selectedSubStrand) return;
    setIsLoading(true);
    setErrorMesg('');
    setAlertMesg('');
    try {
      const response = await fetch(`${API_BASE}api/setlinks/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          elCurriculum,
          subject,
          year,
          strand:       selectedSubStrand.Strand,
          subStrand:    selectedSubStrand.SubStrand,
          dicLsnCodes:  Array.from(selectedDicLsns).join(','),
        }),
      });
      if (!response.ok) throw new Error('Failed to set links');
      const data = await response.json();
      if (data.success) {
        setAlertMesg('Links set successfully!');
        await fetchSubStrands();  // refresh link counts in left panel
      }
    } catch (error) {
      console.error('Failed to set links:', error);
      setErrorMesg('Failed to set links. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetLessonFlag = async (
    dicLsnCode: string,
    elOrMD: string,
    checked: boolean
  ) => {
    setIsLoading(true);
    setErrorMesg('');
    try {
      // Data: DicFileName^LineNum^LessonCode^ELCurriculum^Subject^Year
      const response = await fetch(`${API_BASE}api/setlessonflag/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data:      `${dicLsnCode}^${elCurriculum}^${subject}^${year}`,
          elOrMD,
          queryType: checked ? 'I' : 'D',
        }),
      });
      if (!response.ok) throw new Error('Failed to set lesson flag');
      const result = await response.json();
      if (result !== 'Success') {
        setErrorMesg(result);
      } else {
        await fetchDicDetails();
      }
    } catch (error) {
      console.error('Failed to set lesson flag:', error);
      setErrorMesg('Failed to set lesson flag. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateTimings = async (
    lessonTimeValue: string,
    updateFor: string,
    dicFileName: string,
    unit: string,
    chapter: string,
    lineNum: string
  ) => {
    if (!lessonTimeValue.trim()) {
      setAlertMesg('Please enter lesson time.');
      return;
    }
    if (isNaN(Number(lessonTimeValue))) {
      setAlertMesg('Lesson timing must be numeric.');
      return;
    }
    setIsLoading(true);
    setErrorMesg('');
    setAlertMesg('');
    try {
      const response = await fetch(`${API_BASE}api/updatetimings/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lessonTime: lessonTimeValue, updateFor, dicFileName, unit, chapter, lineNum }),
      });
      if (!response.ok) throw new Error('Failed to update timings');
      const result = await response.json();
      if (result !== 'Success') {
        setErrorMesg(result);
      } else {
        setAlertMesg('Timings updated successfully!');
        await fetchDicDetails();
      }
    } catch (error) {
      console.error('Failed to update timings:', error);
      setErrorMesg('Failed to update timings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderDicRow = (detail: DicDetail, index: number) => {
    const dicLsnCode = `${detail.DicFileName}^${detail.LineNum}^${detail.LessonCode}`;
    const isSelected = selectedDicLsns.has(dicLsnCode);

    const colD: React.CSSProperties       = { borderRight: '1px solid #80a0ff', padding: '3px', textAlign: 'center', width: '3%' };
    const colDesc: React.CSSProperties    = { padding: '3px', paddingLeft: '10px', width: '40%' };
    const colFlag: React.CSSProperties    = { borderLeft: '1px solid #80a0ff', padding: '3px', textAlign: 'center', width: '6%' };
    const colTiming: React.CSSProperties  = { borderLeft: '1px solid #80a0ff', padding: '3px', textAlign: 'center', width: '12%' };
    const colSmall: React.CSSProperties   = { borderLeft: '1px solid #80a0ff', padding: '3px', textAlign: 'center', width: '8%' };

    const timingInput = (lbl: string) => (
      <>
        <input
          type="text"
          id={`txtTime-${index}`}
          style={{ width: '30px', border: '1px solid #999', textAlign: 'center', padding: '2px', marginRight: '3px' }}
        />
        <button
          style={{ padding: '2px 8px', fontSize: '9pt' }}
          onClick={() => {
            const input = document.getElementById(`txtTime-${index}`) as HTMLInputElement;
            const [uf, u, ch, ln] = lbl.split('|');
            handleUpdateTimings(input.value, uf, detail.DicFileName, u, ch, ln);
          }}
        >Update</button>
      </>
    );

    switch (detail.RowType) {
      case 'dicfile':
        return (
          <tr key={`dic-${index}`} style={{ fontSize: '10pt', backgroundColor: '#F0F8FF' }}>
            <td style={colD}></td>
            <td style={{ ...colDesc, fontWeight: 'bold', color: 'blue', fontSize: '13pt' }}>{detail.DicFileName}</td>
            <td style={colFlag}></td>
            <td style={colFlag}></td>
            <td style={colTiming}>
              <input type="text" id={`txtTime-${index}`} style={{ width: '30px', border: '1px solid #999', textAlign: 'center', padding: '2px', marginRight: '3px' }} />
              <button style={{ padding: '2px 8px', fontSize: '9pt' }} onClick={() => { const inp = document.getElementById(`txtTime-${index}`) as HTMLInputElement; handleUpdateTimings(inp.value, 'DICFILE', detail.DicFileName, '', '', detail.LineNum); }}>Update</button>
            </td>
            <td style={colSmall}></td>
            <td style={colSmall}></td>
            <td style={colSmall}></td>
          </tr>
        );

      case 'unit':
        return (
          <tr key={`unit-${index}`} style={{ fontSize: '10pt', backgroundColor: '#FAFAFA' }}>
            <td style={colD}></td>
            <td style={{ ...colDesc, paddingLeft: '15px', fontWeight: 'bold' }}>{detail.Unit}</td>
            <td style={colFlag}></td>
            <td style={colFlag}></td>
            <td style={colTiming}>
              <input type="text" id={`txtTime-${index}`} style={{ width: '30px', border: '1px solid #999', textAlign: 'center', padding: '2px', marginRight: '3px' }} />
              <button style={{ padding: '2px 8px', fontSize: '9pt' }} onClick={() => { const inp = document.getElementById(`txtTime-${index}`) as HTMLInputElement; handleUpdateTimings(inp.value, 'UNIT', detail.DicFileName, detail.UnitCode, '', detail.LineNum); }}>Update</button>
            </td>
            <td style={colSmall}></td>
            <td style={colSmall}></td>
            <td style={colSmall}></td>
          </tr>
        );

      case 'chapter':
        return (
          <tr key={`chapter-${index}`} style={{ fontSize: '10pt', backgroundColor: '#fff' }}>
            <td style={colD}></td>
            <td style={{ ...colDesc, paddingLeft: '30px', fontWeight: 'bold' }}>{detail.Chapter}</td>
            <td style={colFlag}></td>
            <td style={colFlag}></td>
            <td style={colTiming}>
              <input type="text" id={`txtTime-${index}`} style={{ width: '30px', border: '1px solid #999', textAlign: 'center', padding: '2px', marginRight: '3px' }} />
              <button style={{ padding: '2px 8px', fontSize: '9pt' }} onClick={() => { const inp = document.getElementById(`txtTime-${index}`) as HTMLInputElement; handleUpdateTimings(inp.value, 'CHAPTER', detail.DicFileName, detail.UnitCode, detail.ChapterCode, detail.LineNum); }}>Update</button>
            </td>
            <td style={colSmall}></td>
            <td style={colSmall}></td>
            <td style={colSmall}></td>
          </tr>
        );

      case 'lesson': {
        const isLinked = detail.TimesLinked !== null && detail.TimesLinked > 0;
        return (
          <tr key={`lesson-${index}`} style={{ fontSize: '10pt', backgroundColor: index % 2 === 0 ? 'white' : '#f4faff' }}>
            <td style={colD}>
              <input type="checkbox" checked={isSelected} onChange={() => handleDicLsnToggle(dicLsnCode)} />
            </td>
            <td style={colDesc}>
              {detail.Activity?.trim() && (
                <span style={{ color: isLinked ? 'black' : 'red' }}>{detail.Activity}</span>
              )}
            </td>
            <td style={colFlag}>
              <input type="checkbox" checked={detail.ELFlagged} onChange={(e) => handleSetLessonFlag(dicLsnCode, 'EL', e.target.checked)} />
            </td>
            <td style={colFlag}>
              <input type="checkbox" checked={detail.MDFlagged} onChange={(e) => handleSetLessonFlag(dicLsnCode, 'MD', e.target.checked)} />
            </td>
            <td style={colTiming}>
              <input type="text" id={`txtTime-${index}`} defaultValue={detail.LessonTime ?? ''} style={{ width: '30px', border: '1px solid #999', textAlign: 'center', padding: '2px', marginRight: '3px' }} />
              <button style={{ padding: '2px 8px', fontSize: '9pt' }} onClick={() => { const inp = document.getElementById(`txtTime-${index}`) as HTMLInputElement; handleUpdateTimings(inp.value, 'LESSON', detail.DicFileName, detail.UnitCode, detail.ChapterCode, detail.LineNum); }}>Update</button>
            </td>
            <td style={colSmall}>{detail.TimesLinked ?? ''}</td>
            <td style={colSmall}>
              <input type="checkbox" checked={detail.IgnoreLesson} onChange={(e) => handleSetLessonFlag(dicLsnCode, 'IgnoreLesson', e.target.checked)} />
            </td>
            <td style={colSmall}>
              <input type="checkbox" checked={detail.SelectiveLesson} onChange={(e) => handleSetLessonFlag(dicLsnCode, 'SelectiveLSns', e.target.checked)} />
            </td>
          </tr>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div style={{ margin: '0px', fontFamily: 'Arial', fontSize: '11pt', backgroundColor: '#fff' }}>
      {isLoading && <Loading />}
      {errorMesg && (
        <div style={{ backgroundColor: '#ffe0e0', border: '1px solid red', padding: '6px', marginBottom: '4px', color: 'red', fontSize: '10pt' }}>
          {errorMesg}
        </div>
      )}
      {alertMesg && (
        <div style={{ backgroundColor: '#fffbe6', border: '1px solid orange', padding: '6px', marginBottom: '4px', color: '#805000', fontSize: '10pt' }}>
          {alertMesg}
        </div>
      )}
      <table style={{ width: '99%', border: '0', borderCollapse: 'collapse' }} cellPadding="0" cellSpacing="0">
        <tbody>
          <tr>
            <td align="center" colSpan={2} style={{ backgroundColor: 'maroon', color: 'white', fontWeight: 'bold', padding: '1px', fontSize: '12pt' }}>
              Linking of Sub-strands with elearnWeb Activities
            </td>
          </tr>
          <tr><td colSpan={2} style={{ height: '3px' }}></td></tr>
          <tr>
            <td align="center" colSpan={2} style={{ backgroundColor: '#ffffcc', padding: '5px', border: '1px solid black' }}>
              <span style={{ fontWeight: 'normal', fontSize: '12pt' }}>Year: </span>
              <select value={year} onChange={(e) => setYear(e.target.value)} style={{ fontSize: '12pt', fontFamily: 'Arial', padding: '0px', cursor: 'pointer' }}>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <span style={{ fontWeight: 'normal', fontSize: '12pt' }}>Country: </span>
              {/* Dropdown shows display name; elCurriculum IS the display name from EL_MD_DicCountryYear */}
              <select value={elCurriculum} onChange={(e) => setElCurriculum(e.target.value)} style={{ fontSize: '12pt', fontFamily: 'Arial', padding: '0px', cursor: 'pointer' }}>
                {countries.map((c) => <option key={c.value} value={c.value}>{c.text}</option>)}
              </select>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <span style={{ fontWeight: 'normal', fontSize: '12pt' }}>Subject: </span>
              <select value={subject} onChange={(e) => setSubject(e.target.value)} style={{ fontSize: '12pt', fontFamily: 'Arial', padding: '0px', cursor: 'pointer' }}>
                {subjects.map((sub) => <option key={sub.value} value={sub.value}>{sub.text}</option>)}
              </select>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <button onClick={handleGoClick} style={{ cursor: 'pointer', padding: '0px 16px', fontSize: '10pt', width: '80px' }} disabled={isLoading}>
              GO
              </button>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <button onClick={() => window.location.href = '/sysadmin'} style={{ cursor: 'pointer', padding: '0px 10px', fontSize: '10pt' }}>
                Back to Menu
              </button>
            </td>
          </tr>
          <tr><td colSpan={2} style={{ height: '5px' }}></td></tr>

          {(subStrands.length > 0 || dicDetails.length > 0) && (
            <>
              <tr style={{ verticalAlign: 'top', backgroundColor: '#fff' }}>
                {/* Sub-Strands panel */}
                <td style={{ width: '50%', height: '69vh', border: '1px solid #80a0ff' }}>
                  <div style={{ backgroundColor: '#c7ddfd', padding: '5px', fontWeight: 'bold', fontSize: '10pt', color: '#2859ac', borderBottom: '1px solid #80a0ff', textAlign: 'center' }}>
                    Sub-Strands
                  </div>
                  <div style={{ height: '65vh', overflowY: 'auto', backgroundColor: 'white' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead style={{ position: 'sticky', top: 0, backgroundColor: '#c7ddfd', zIndex: 1, borderBottom: '1px solid #80a0ff' }}>
                        <tr style={{ fontSize: '10pt', color: '#2859ac', fontWeight: 'normal' }}>
                          <th style={{ padding: '6px', textAlign: 'center', fontWeight: 'normal', width: '2%',  borderLeft: '1px solid #80a0ff' }}>Strand</th>
                          <th style={{ padding: '6px', textAlign: 'center', fontWeight: 'normal', width: '5%',  borderLeft: '1px solid #80a0ff' }}>SubStrand</th>
                          <th style={{ padding: '6px', textAlign: 'left',   fontWeight: 'normal', width: '27%', borderLeft: '1px solid #80a0ff' }}>Description</th>
                          <th style={{ padding: '6px', textAlign: 'center', fontWeight: 'normal', width: '55%', borderLeft: '1px solid #80a0ff' }}>Lessons<br />Linked</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subStrands.map((ss, index) => (
                          <tr
                            key={index}
                            onClick={() => handleSubStrandClick(ss)}
                            style={{
                              borderBottom:
                                index === subStrands.length - 1
                                  ? '1px solid #80a0ff'
                                  : 'none',
                              cursor: 'pointer',
                              backgroundColor:
                                selectedSubStrand?.Strand === ss.Strand && selectedSubStrand?.SubStrand === ss.SubStrand
                                  ? '#FFA500'
                                  : index % 2 === 0 ? '#f4faff' : 'white',
                              color:
                                selectedSubStrand?.Strand === ss.Strand && selectedSubStrand?.SubStrand === ss.SubStrand
                                  ? 'white'
                                  : ss.NoOfELMDMWLinks === 0 ? 'red' : 'black',
                              fontSize: '10pt',
                              height: '25px',
                            }}
                          >
                            <td style={{ padding: '4px', textAlign: 'center', borderRight: '1px solid #80a0ff' }}>{ss.Strand}</td>
                            <td style={{ padding: '4px', textAlign: 'center', borderRight: '1px solid #80a0ff' }}>{ss.SubStrand}</td>
                            <td style={{ padding: '4px', borderRight: '1px solid #80a0ff' }}>{ss.Description}</td>
                            <td style={{ padding: '4px', textAlign: 'center' }}>{ss.NoOfELMDMWLinks || ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </td>

                {/* Activities panel */}
                <td style={{ width: '50%', height: '69vh', border: '1px solid #80a0ff' }}>
                  <div style={{ backgroundColor: '#c7ddfd', padding: '5px', fontWeight: 'bold', fontSize: '10pt', color: '#2859ac', borderBottom: '1px solid #80a0ff', textAlign: 'center' }}>
                    Activities for {elCurriculum} - {subject} - Year {year}
                  </div>
                  <div style={{ height: '65vh', overflowY: 'auto', backgroundColor: 'white' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10pt' }}>
                      <thead style={{ position: 'sticky', top: 0, backgroundColor: '#c7ddfd', zIndex: 1 }}>
                        <tr style={{ fontSize: '10pt', color: '#2859ac', fontWeight: 'normal' }}>
                          <th style={{ padding: '4px', textAlign: 'center', fontWeight: 'normal', borderLeft: '1px solid #80a0ff', borderBottom: '1px solid #80a0ff', width: '3%' }} rowSpan={2}></th>
                          <th style={{ padding: '4px', textAlign: 'left',   fontWeight: 'normal', borderLeft: '1px solid #80a0ff', borderBottom: '1px solid #80a0ff', width: '40%' }} rowSpan={2}>Description</th>
                          <th style={{ padding: '4px', textAlign: 'center', fontWeight: 'normal', borderLeft: '1px solid #80a0ff', borderBottom: '1px solid #80a0ff' }} colSpan={2}>Flagged</th>
                          <th style={{ padding: '4px', textAlign: 'center', fontWeight: 'normal', borderLeft: '1px solid #80a0ff', borderBottom: '1px solid #80a0ff', width: '15%' }} rowSpan={2}>LessonTimings</th>
                          <th style={{ padding: '4px', textAlign: 'center', fontWeight: 'normal', borderLeft: '1px solid #80a0ff', borderBottom: '1px solid #80a0ff', width: '12%' }} rowSpan={2}>Times<br />Linked</th>
                          <th style={{ padding: '4px', textAlign: 'center', fontWeight: 'normal', borderLeft: '1px solid #80a0ff', borderBottom: '1px solid #80a0ff', width: '12%' }} rowSpan={2}>IgnoreLesson</th>
                          <th style={{ padding: '4px', textAlign: 'center', fontWeight: 'normal', borderLeft: '1px solid #80a0ff', borderBottom: '1px solid #80a0ff', width: '25%' }} rowSpan={2}>SelectiveLSns</th>
                        </tr>
                        <tr style={{ fontSize: '9pt', color: '#2859ac', fontWeight: 'normal', backgroundColor: '#c7ddfd' }}>
                          <th style={{ padding: '4px', textAlign: 'center', fontWeight: 'normal', borderLeft: '1px solid #80a0ff', borderBottom: '1px solid #80a0ff', width: '1%' }}>EL</th>
                          <th style={{ padding: '4px', textAlign: 'center', fontWeight: 'normal', borderLeft: '1px solid #80a0ff', borderBottom: '1px solid #80a0ff', width: '1%' }}>MD</th>
                        </tr>
                      </thead>
                      <tbody style={{  borderBottom: '1px solid #80a0ff'}}>
                        {dicDetails.map((detail, index) => renderDicRow(detail, index))}
                      </tbody>
                    </table>
                  </div>
                </td>
              </tr>
              <tr><td colSpan={2} style={{ height: '3px' }}></td></tr>

              {selectedSubStrand && (
                <tr>
                  <td align="left" valign="top" style={{ backgroundColor: '#ffffff', padding: '8px', border: '1px solid blue', width: '50%' }}>
                    <div style={{ fontWeight: 'normal', marginBottom: '0px', textDecoration: 'underline', fontSize: '12pt' }}>Selected Sub-strand</div>
                    <div style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '12pt' }}>{selectedSubStrand.Description}</div>
                    <button
                      onClick={handleSetLinks}
                      style={{ display: selectedDicLsns.size > 0 ? '' : 'none', padding: '1px 10px', fontSize: '10pt', cursor: 'pointer' }}
                      disabled={isLoading}
                    >
                      Link Data
                    </button>
                  </td>
                  <td align="left" style={{ backgroundColor: '#fff', padding: '8px', border: '1px solid blue', width: '50%' }}>
                    <div style={{ fontWeight: 'normal', marginBottom: '1px', textDecoration: 'underline', fontSize: '12pt' }}>Selected Lessons</div>
                    <div style={{ height: '100px', overflowY: 'auto', border: 'none', fontSize: '12pt' }}>
                      {selectedDicLsns.size === 0 ? (
                        <div style={{ color: '#999', fontStyle: 'italic' }}>No lessons selected</div>
                      ) : (
                        Array.from(selectedDicLsns).map((lsn, index) => {
                          const parts = lsn.split('^');
                          const lesson =
                            dicDetails.find(d =>
                              d.RowType === 'lesson' &&
                              d.DicFileName === parts[0] &&
                              d.LessonCode === parts[2]
                            ) ||
                            linkedLessons.find(l =>
                              l.DicFileName === parts[0] &&
                              l.LessonCode === parts[2]
                            );
                          return (
                            <div key={index}>
                              {index + 1}) {lesson
                                ? lesson.Activity?.trim() ? lesson.Activity : '(No Activity Name)'
                                : '(Missing lesson in dictionary)'}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default EL_LinkMDandMW;